import { useTranslation } from "react-i18next";
import { SubscriptionType } from "@/types/subscriptionType";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrency } from "@/contexts/CurrencyContext";

interface SubscriptionTypesTableProps {
  types: SubscriptionType[];
  isLoading: boolean;
  onEdit: (type: SubscriptionType) => void;
  onDelete: (type: SubscriptionType) => void;
}

export function SubscriptionTypesTable({
  types,
  isLoading,
  onEdit,
  onDelete,
}: SubscriptionTypesTableProps) {
  const { t } = useTranslation("subscriptions");
  const { formatCurrency } = useCurrency();

  const formatPrice = (price: number) => formatCurrency(price);

  const formatValidity = (days: number | null) => {
    if (!days) return t("types.unlimited");
    if (days === 30) return "1 mois";
    if (days === 60) return "2 mois";
    if (days === 90) return "3 mois";
    if (days === 365) return "1 an";
    return `${days} jours`;
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (types.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>{t("types.empty")}</p>
        <p className="text-sm mt-1">{t("types.emptyHint")}</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("types.name")}</TableHead>
            <TableHead className="text-center">{t("types.sessions")}</TableHead>
            <TableHead className="text-center">{t("types.validity")}</TableHead>
            <TableHead className="text-center">{t("types.jokers")}</TableHead>
            <TableHead className="text-right">{t("types.price")}</TableHead>
            <TableHead className="text-center">{t("types.status")}</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {types.map((type) => (
            <TableRow key={type.id}>
              <TableCell>
                <div>
                  <p className="font-medium">{type.name}</p>
                  {type.description && (
                    <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {type.description}
                    </p>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-center">{type.sessions_total}</TableCell>
              <TableCell className="text-center">{formatValidity(type.validity_days)}</TableCell>
              <TableCell className="text-center">{type.jokers_total}</TableCell>
              <TableCell className="text-right font-medium">
                {formatPrice(type.price)}
              </TableCell>
              <TableCell className="text-center">
                <Badge variant={type.is_active ? "default" : "secondary"}>
                  {type.is_active ? t("types.active") : t("types.inactive")}
                </Badge>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(type)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      {t("types.edit")}
                    </DropdownMenuItem>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                          onSelect={(e) => e.preventDefault()}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t("types.delete")}
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("types.deleteConfirmTitle")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("types.deleteConfirmDescription", { name: type.name })}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("types.cancel")}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(type)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {t("types.confirmDelete")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
